import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useCallback } from "react";

import { trackBookingConfirmationConversion } from "@/utils/Analytics/Conversion";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import {
  buildBookingConfirmationAnalyticsData,
  readBookingConfirmationAnalyticsData,
} from "@/utils/Analytics/bookings/buildBookingConfirmationAnalyticsData";
import { useMutationWithError } from "@/utils/hooks/useMutationWithError";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { bookingSummaryAnalyticsDataAtom } from "../../BookingSummary/atoms/analytics/bookingSummaryAnalyticsDataAtom";
import type {
  ConfirmDraftBookingInput,
  useCompleteDiningReservationMutation,
} from "./__generated__/useCompleteDiningReservationMutation.graphql";

type CompleteDiningReservationArgs = {
  input: ConfirmDraftBookingInput;
  onCompleted?: () => void;
};

export const useCompleteDiningReservation = () => {
  const [commit, isLoading] =
    useMutationWithError<useCompleteDiningReservationMutation>(
      useCompleteDiningReservationMutationString
    );
  const { showToast } = useToast();
  const analyticsData = useAtomValue(bookingSummaryAnalyticsDataAtom);
  const trackEvent = useTrackEvent();

  const completeDiningReservation = useCallback(
    ({ input, onCompleted }: CompleteDiningReservationArgs) => {
      commit({
        variables: { input },
        onCompleted: (res) => {
          const confirmedBooking = readBookingConfirmationAnalyticsData(
            res.responsePayload?.booking
          );
          trackBookingConfirmationConversion({
            success: res.responsePayload?.success,
            eventName: "dining_reservation_confirmed",
            analyticsData,
            bookings: confirmedBooking ? [confirmedBooking] : [],
          });
          trackEvent(
            "BookingDetail.DraftBookingConfirmed",
            buildBookingConfirmationAnalyticsData(
              analyticsData,
              confirmedBooking ? [confirmedBooking] : []
            ) ?? {}
          );
          showToast({
            title: t`Reservation completed`,
            semanticStyle: "success",
            toastId: "complete-dining-reservation-success",
          });
          onCompleted?.();
        },
      });
    },
    [analyticsData, commit, showToast, trackEvent]
  );

  return [completeDiningReservation, isLoading] as const;
};

const useCompleteDiningReservationMutationString = graphql`
  mutation useCompleteDiningReservationMutation(
    $input: ConfirmDraftBookingInput!
  ) {
    responsePayload: confirmDraftBooking(input: $input) {
      success
      errors {
        code

        message
      }
      booking {
        id
        ...buildBookingConfirmationAnalyticsData_booking
        ... on DiningBooking {
          status
        }
      }
    }
  }
`;
